import { useId, useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useTimeFieldState } from '@react-stately/datepicker';
import { useTimeField, useDateSegment } from '@react-aria/datepicker';
import { useLocale } from '@react-aria/i18n';
import { parseTime } from '@internationalized/date';
import { useTranslation } from '@hooks/useTranslation';
import type { TimeValue } from '@react-aria/datepicker';
import type { DateFieldState, DateSegment } from '@react-stately/datepicker';
import type { InputSize } from '@components/ui/Input/TextInput';
import FormField from '@components/ui/Input/FormField';
import styles from './TimePicker.module.scss';
import inputStyles from '@components/ui/Input/Input.module.scss';

// ============================================
// TYPES
// ============================================

/** Props for the TimePicker component. */
export interface TimePickerProps {
  /** Selected time as string: 'HH:mm' or 'HH:mm:ss' */
  value?: string;
  /** Called when the time changes, receives 'HH:mm' or 'HH:mm:ss' string */
  onChange?: (value: string) => void;
  /** Label text displayed above the input */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the input (hidden when error is present) */
  helperText?: string;
  /** Error message — replaces helperText, applies error border */
  error?: string;
  /** Placeholder text (segments show placeholders natively) */
  placeholder?: string;
  /** Input size variant */
  size?: InputSize;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
  /** Whether the time picker is disabled */
  disabled?: boolean;
  /** 12-hour or 24-hour clock */
  hourCycle?: 12 | 24;
  /** Smallest time unit to display */
  granularity?: 'hour' | 'minute' | 'second';
  /** Minimum allowed time as string: 'HH:mm' or 'HH:mm:ss' */
  minValue?: string;
  /** Maximum allowed time as string: 'HH:mm' or 'HH:mm:ss' */
  maxValue?: string;
  /** Interval in minutes for the time dropdown (default: 30) */
  timeIntervals?: number;
  /** Show the scrollable time dropdown on clock icon click (default: true) */
  showDropdown?: boolean;
  /** Additional className on the outer wrapper */
  className?: string;
  /** Override auto-generated ID */
  id?: string;
  /** Forwarded ref to the field group element */
  ref?: React.Ref<HTMLDivElement>;
}

// ============================================
// HELPERS — ISO time string <-> Time
// ============================================

function toTimeValue(str: string | undefined): TimeValue | undefined {
  if (!str) return undefined;
  try {
    return parseTime(str);
  } catch {
    return undefined;
  }
}

/** Get current time as 'HH:mm' string */
function getCurrentTime(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Generate time slots for the dropdown */
function generateTimeSlots(
  intervalMinutes: number,
  hourCycle: 12 | 24 | undefined,
  minTime?: TimeValue,
  maxTime?: TimeValue,
): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = [];
  const is12h = hourCycle === 12;
  const totalMinutesInDay = 24 * 60;

  for (let m = 0; m < totalMinutesInDay; m += intervalMinutes) {
    const hour = Math.floor(m / 60);
    const minute = m % 60;
    const hh = String(hour).padStart(2, '0');
    const mm = String(minute).padStart(2, '0');
    const isoValue = `${hh}:${mm}`;

    // Filter by min/max
    if (minTime) {
      const minMinutes = minTime.hour * 60 + minTime.minute;
      if (m < minMinutes) continue;
    }
    if (maxTime) {
      const maxMinutes = maxTime.hour * 60 + maxTime.minute;
      if (m > maxMinutes) continue;
    }

    let label: string;
    if (is12h) {
      const period = hour < 12 ? 'AM' : 'PM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      label = `${displayHour}:${mm} ${period}`;
    } else {
      label = `${hh}:${mm}`;
    }

    slots.push({ value: isoValue, label });
  }

  return slots;
}

// ============================================
// CLOCK ICON
// ============================================

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle
      cx="8"
      cy="8"
      r="6.5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M8 4.5v4l2.5 1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================
// TIME PICKER COMPONENT
// ============================================

/**
 * A time input component with individually editable segments (hour, minute, second, AM/PM)
 * and a scrollable time dropdown triggered by the clock icon.
 * Accepts and emits time strings ('HH:mm' or 'HH:mm:ss') for easy API/form integration.
 * Built on react-aria for full accessibility and keyboard navigation.
 */
function TimePicker({
  ref,
  value,
  onChange,
  label,
  isRequired = false,
  helperText,
  error,
  placeholder: _placeholder,
  size = 'md',
  fullWidth = true,
  disabled = false,
  hourCycle,
  granularity = 'minute',
  minValue,
  maxValue,
  timeIntervals = 30,
  showDropdown = true,
  className,
  id: idProp,
}: TimePickerProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const hasError = Boolean(error);
  const { locale } = useLocale();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const parsedValue = useMemo(() => toTimeValue(value) ?? null, [value]);
  const parsedMinValue = useMemo(() => toTimeValue(minValue), [minValue]);
  const parsedMaxValue = useMemo(() => toTimeValue(maxValue), [maxValue]);

  const handleChange = useCallback(
    (time: TimeValue | null) => {
      if (time && onChange) {
        onChange(time.toString());
      }
    },
    [onChange],
  );

  const stateProps = {
    value: parsedValue,
    onChange: handleChange,
    isDisabled: disabled,
    isRequired,
    hourCycle,
    granularity,
    minValue: parsedMinValue,
    maxValue: parsedMaxValue,
    locale,
  };

  const state = useTimeFieldState(stateProps);

  const ariaProps = {
    label: label ?? 'Time',
    value: parsedValue,
    onChange: handleChange,
    isDisabled: disabled,
    isRequired,
    hourCycle,
    granularity,
    minValue: parsedMinValue,
    maxValue: parsedMaxValue,
    'aria-label': label ?? 'Time',
  };

  const { fieldProps } = useTimeField(ariaProps, state, fieldRef);

  const describedBy = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const sizeClass = inputStyles[size] ?? inputStyles.md;

  const fieldClassName = [sizeClass, fullWidth && inputStyles.fullWidth, className]
    .filter(Boolean)
    .join(' ');

  const inputClasses = [
    styles.timeInput,
    inputStyles.inputBase,
    hasError && styles.timeInputError,
    disabled && styles.timeInputDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  // Merge refs
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      (fieldRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  const handleClockClick = useCallback(() => {
    if (!disabled && showDropdown) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled, showDropdown]);

  const handleSlotSelect = useCallback(
    (slotValue: string) => {
      if (onChange) {
        onChange(slotValue);
      }
      setIsOpen(false);
    },
    [onChange],
  );

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
      className={fieldClassName}
    >
      <div className={styles.inputWrapper}>
        <div className={inputClasses} aria-describedby={describedBy}>
          <div {...fieldProps} ref={setRefs} className={styles.segmentsWrapper}>
            {state.segments.map((segment, i) => (
              <TimeSegmentView key={i} segment={segment} state={state} />
            ))}
          </div>
        </div>
        <button
          type="button"
          className={styles.clockButton}
          onClick={handleClockClick}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Open time selector"
        >
          <ClockIcon />
        </button>

        {isOpen && showDropdown && (
          <TimeDropdown
            slots={generateTimeSlots(timeIntervals, hourCycle, parsedMinValue ?? undefined, parsedMaxValue ?? undefined)}
            selectedValue={value}
            onSelect={handleSlotSelect}
            onClose={() => setIsOpen(false)}
          />
        )}
      </div>
    </FormField>
  );
}

// ============================================
// TIME SEGMENT — individual editable segment
// ============================================

interface TimeSegmentViewProps {
  segment: DateSegment;
  state: DateFieldState;
}

function TimeSegmentView({ segment, state }: TimeSegmentViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const isLiteral = segment.type === 'literal';

  return (
    <span
      {...segmentProps}
      ref={ref}
      className={isLiteral ? styles.separator : styles.segment}
      data-placeholder={segment.isPlaceholder || undefined}
    >
      {segment.text}
    </span>
  );
}

// ============================================
// TIME DROPDOWN — scrollable time slot list
// ============================================

interface TimeDropdownProps {
  slots: { value: string; label: string }[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  onClose: () => void;
}

function TimeDropdown({ slots, selectedValue, onSelect, onClose }: TimeDropdownProps) {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Scroll selected item into view on open
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' });
    }
  }, []);

  // Normalize selected value for comparison (strip seconds if present)
  const normalizedSelected = selectedValue?.substring(0, 5);

  return (
    <div ref={dropdownRef} className={styles.dropdown} role="listbox" aria-label="Select time">
      <button
        type="button"
        className={styles.nowButton}
        onClick={() => onSelect(getCurrentTime())}
      >
        {t('K_BUTTON_NOW', 'Now')}
      </button>
      <div className={styles.dropdownDivider} />
      {slots.map((slot) => {
        const isSelected = normalizedSelected === slot.value;
        const itemClasses = [
          styles.dropdownItem,
          isSelected && styles.dropdownItemSelected,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={slot.value}
            ref={isSelected ? selectedRef : undefined}
            type="button"
            role="option"
            aria-selected={isSelected}
            className={itemClasses}
            onClick={() => onSelect(slot.value)}
          >
            {slot.label}
          </button>
        );
      })}
    </div>
  );
}

export default TimePicker;
